/**
 * @ngdoc Controller
 * @name DynamicESSReportingController
 */
import { handleFileDownload } from "@/utils/download";
(function() {
    "use strict";

    angular
        .module("vrm.core")
        .constant("DynamicESSReportingConstant", {
            FILENAME_PREFIX: "dynamic-ess-report",
            ERROR_TIMEOUT: 30000 // 30 seconds
        })
        .controller(
            "DynamicESSReportingController",
            DynamicESSReportingController
        );

    /**
     * @constructor
     * @ngInject
     */
    function DynamicESSReportingController(
        $timeout,
        locale,
        $scope,
        ApiService,
        DynamicESSReportingConstant,
        VrmModalService,
        vrmNotificationsService,
        vrmNotificationType
    ) {
        $scope.downloading = false;
        $scope.datePicker = {
            range: {},
            control: {}
        };
        $scope.report = {};
        $scope.showReport = false;
        $scope.loading = false;

        $scope.$watch("downloading", function() {
            if ($scope.downloading) {
                $timeout(function() {
                    VrmModalService.openDownloadModal($scope);
                }, 10); // 10 milliseconds
            } else {
                $.magnificPopup.close();
            }
        });

        locale.ready("common", "core").then(function() {
            $scope.generateReport = function() {
                loadData();
            };

            function round(value, decimal) {
                if (isNaN(value) || value === null || value === undefined) {
                    return "-";
                }

                const rounded = parseFloat(value.toFixed(decimal));

                // Handle -0 to return clean 0
                return rounded === 0 ? 0 : rounded.toFixed(decimal);
            }

            function formatting(obj, decimal = 2) {
                let result = {};

                Object.entries(obj).forEach(([key, value]) => {
                    switch (typeof value) {
                        case "number":
                            switch (key) {
                                case "countDaysMinus":
                                case "countHoursMinus":
                                    decimal = 0;
                                    break;
                                case "percentage":
                                    decimal = 1;
                                    break;
                                case "totalCost":
                                case "diffTotalCost":
                                    decimal = 2;
                                    break;
                                default:
                                    break;
                            }
                            result[key] = round(value, decimal);
                            break;
                        case "object":
                            if (value) {
                                switch (key) {
                                    case "price":
                                        decimal = 2;
                                        break;
                                    case "comparison":
                                        decimal = 1;
                                        break;
                                    default:
                                        decimal = 0;
                                        break;
                                }
                                result[key] = formatting(value, decimal);
                            } else {
                                result[key] = value;
                            }
                            break;
                        case "boolean": // only `false` returned from API
                            result[key] = "-";
                            break;
                        default:
                            result[key] = value;
                    }
                });

                return result;
            }

            function generateFilenameTimestamp() {
                const now = new Date();
                // Create `ddmmyyyy_His` datetime format
                return new Intl.DateTimeFormat("en-GB", {
                    dateStyle: "short",
                    timeStyle: "medium"
                })
                    .format(now)
                    .replace(/\//g, "")
                    .replace(/:/g, "")
                    .replace(", ", "_");
            }

            function getReportParameters() {
                return {
                    type: "descriptive",
                    start: $scope.datePicker.range.startDate,
                    end: $scope.datePicker.range.endDate
                };
            }

            function loadData() {
                if (
                    !$scope.installation ||
                    !$scope.datePicker.range.startDate
                ) {
                    return;
                }
                $scope.loading = true;
                ApiService.installations(
                    $scope.installation.id,
                    "dynamic-ess-report",
                    getReportParameters()
                )
                    .then(function(response) {
                        $scope.report = formatting(response.data.report);
                        $scope.showReport = true;
                        $scope.loading = false;
                    })
                    .catch(function() {
                        $scope.showReport = false;
                        $scope.loading = false;
                    });
            }

            $scope.downloadReport = function(format) {
                let params = getReportParameters();
                params.download = format;

                if ($scope.downloading) {
                    locale.ready("advanced").then(function() {
                        vrmNotificationsService.add({
                            type: vrmNotificationType.ERROR,
                            message: locale.getString(
                                "advanced.download_pending"
                            )
                        });
                    });
                    return;
                }

                $scope.downloading = true;

                ApiService.installationDownload(
                    $scope.installation.id,
                    "dynamic-ess-report",
                    params,
                    true
                )
                    .then(function(response) {
                        const filename = `${
                            DynamicESSReportingConstant.FILENAME_PREFIX
                        }-${
                            params.type
                        }_${generateFilenameTimestamp()}.${format.toLowerCase()}`;
                        handleFileDownload(response.data, filename);
                        $scope.downloading = false;
                    })
                    .catch(function(error) {
                        locale.ready("reporting").then(function() {
                            vrmNotificationsService.add({
                                type: vrmNotificationType.ERROR,
                                message:
                                    error.data.errors ||
                                    error.data.error_code ||
                                    locale.getString(
                                        "reporting.download-error"
                                    ),
                                timeout:
                                    DynamicESSReportingConstant.ERROR_TIMEOUT
                            });
                        });
                        $scope.downloading = false;
                    });
            };

            $scope.getDessDurationPercentage = function(on, total) {
                if (!total) {
                    return "- %";
                }

                return round((on / total) * 100, 1) + "%";
            };

            $scope.getPercentageClassLabel = function(
                value,
                unit = "",
                inverted = false
            ) {
                if (value > 0) {
                    return {
                        class: inverted ? "label-bad" : "label-good",
                        icon: inverted
                            ? "/img/svg-icons/trending_up_red.svg"
                            : "/img/svg-icons/trending_up_green.svg",
                        value: `${value} ${unit}`
                    };
                } else if (value < 0) {
                    return {
                        class: inverted ? "label-good" : "label-bad",
                        icon: inverted
                            ? "/img/svg-icons/trending_down_green.svg"
                            : "/img/svg-icons/trending_down_red.svg",
                        value: `${value} ${unit}`
                    };
                }
                return {
                    class: "label-neutral",
                    icon: "/img/svg-icons/block.svg",
                    value: "No data"
                };
            };

            $scope.saveTemplate = function() {
                VrmModalService.openReportTemplateModal($scope, function(arg) {
                    if (!arg) {
                        vrmNotificationsService.add({
                            type: vrmNotificationType.ERROR,
                            message: locale.getString(
                                "reporting.template_name_required"
                            )
                        });
                    } else {
                        let data = getReportParameters();
                        data.report_type = "dynamic-ess";
                        data.idSite = $scope.installation.id;
                        data.name = arg;

                        ApiService.reportTemplates(
                            $scope.installation.id,
                            null,
                            data,
                            "POST"
                        ).then(response => {
                            if (response.data.errors) {
                                switch (response.data.errors.name) {
                                    case "This value is too short. It should have 2 characters or more.":
                                        locale.ready("reporting").then(
                                            vrmNotificationsService.add({
                                                type: vrmNotificationType.ERROR,
                                                message: locale.getString(
                                                    "reporting.invalid_template_name"
                                                )
                                            })
                                        );
                                        break;
                                    case "reporttemplate.name.must.be.unique.for.installation":
                                        locale.ready("reporting").then(
                                            vrmNotificationsService.add({
                                                type: vrmNotificationType.ERROR,
                                                message: locale.getString(
                                                    "reporting.unique_report_template_name"
                                                )
                                            })
                                        );
                                        break;
                                    default:
                                        vrmNotificationsService.add({
                                            type: vrmNotificationType.ERROR,
                                            message: ApiService.formatErrors(
                                                response
                                            )
                                        });
                                }
                            } else {
                                vrmNotificationsService.add({
                                    type: vrmNotificationType.SUCCESS,
                                    message: locale.getString(
                                        "reporting.template_saved"
                                    )
                                });
                            }
                        });
                    }
                });
            };

            $scope.printReport = function() {
                window.print();
            };
        });
    }
})();
